<template>
    <PageHeader :crumbs="[{ label: $t('portal.categories.title') }]" />

    <div class="tw-p-2">
        <div class="tw-h-full tw-w-full tw-p-4 tw-bg-white tw-rounded tw-border tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
            <div class="tw-justify-between tw-flex tw-items-center tw-gap-2">
                <FormField v-model="search" input-dir="ltr" :placeholder="$t('portal.categories.search')"
                    left-icon="clarity:search-line" type="text" class="tw-min-w-[100px]" hide-errors @keyup.enter="fetch(1)" />
                <a-button left-icon="heroicons:plus-16-solid" @click="openCreate">{{ $t('buttons.create') }}</a-button>
            </div>

            <div class="tw-mt-5">
                <DataTable :columns="columns" :loading="loading" :count="items.length">
                    <tr v-for="c in items" :key="c.id + '-' + c.updated_at" :class="rowClass">
                        <td :class="cellClass"><span class="tw-font-medium tw-text-gray-800">{{ c.name }}</span></td>
                        <td :class="cellClass"><span class="tw-text-gray-500">{{ c.description || '—' }}</span></td>
                        <td :class="cellClass"><span class="tw-text-gray-600">{{ c.products_count ?? 0 }}</span></td>
                        <td :class="cellClass"><StatusBadge :status="c.status" /></td>
                        <td :class="cellClass">
                            <div class="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                                <a-action-button icon="solar:pen-2-bold-duotone" intent="secondary" @click="openEdit(c)" />
                                <a-action-button icon="solar:trash-bin-2-bold-duotone" intent="danger" @click="askDelete(c)" />
                            </div>
                        </td>
                    </tr>
                </DataTable>
            </div>

            <div class="tw-my-4"></div>

            <TablePagination v-if="items.length" :has-next="options.current_page < options.last_page"
                :has-previous="options.current_page > 1" @next="fetch(options.current_page + 1)"
                @previous="fetch(options.current_page - 1)" />
        </div>
    </div>

    <CategoryFormModal :visible="formVisible" :category="editing" @cancel="formVisible = false" @saved="onSaved" />
    <DeleteModal :visible="deleteVisible" :loading="deleting" @cancel="deleteVisible = false" @confirm="confirmDelete" />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { paginate, remove } from '@/api/categories';
import PageHeader from '@/components/PageHeader.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import FormField from '@/components/form/FormField';
import DataTable from '@/components/table/DataTable.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import DeleteModal from '@/components/modals/DeleteModal.vue';
import CategoryFormModal from './CategoryFormModal.vue';
import { rowClass, cellClass } from '@/composables/useTableStyles';
import useAlert from '@/composables/useAlert';

const i18n = useI18n();

const items = ref([]);
const options = ref({ current_page: 1, last_page: 1 });
const loading = ref(false);
const search = ref('');

const formVisible = ref(false);
const editing = ref(null);
const deleteVisible = ref(false);
const deleting = ref(false);
const toDelete = ref(null);

const columns = computed(() => [
    { label: i18n.t('fields.name') },
    { label: i18n.t('fields.description') },
    { label: i18n.t('portal.fields.products'), classes: ['tw-w-[110px]'] },
    { label: i18n.t('fields.status'), classes: ['tw-w-[110px]'] },
    { label: i18n.t('fields.actions'), classes: ['tw-w-[110px]', 'tw-text-end'] },
]);

async function fetch(page = 1) {
    loading.value = true;
    try {
        const { data } = await paginate({ per_page: 10, page, search: search.value });
        const { data: rows, ...ops } = data.data;
        items.value = rows;
        options.value = ops;
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

const openCreate = () => { editing.value = null; formVisible.value = true; };
const openEdit = (c) => { editing.value = { ...c }; formVisible.value = true; };
const onSaved = () => { formVisible.value = false; fetch(options.value.current_page); };

const askDelete = (c) => { toDelete.value = c; deleteVisible.value = true; };
const confirmDelete = async () => {
    deleting.value = true;
    try {
        await remove(toDelete.value.id);
        useAlert(i18n.t('messages.deleted-successfully', { field: i18n.t('portal.entities.category') }));
        deleteVisible.value = false;
        fetch(options.value.current_page);
    } catch (e) { /* interceptor */ } finally {
        deleting.value = false;
    }
};

fetch();
</script>
